import { SectionWrapper } from '@common/components/BookingForms/Section';
import { Button } from '@common/components/Button';
import { TextArea } from '@common/components/FormElements/Textarea';
import Portal from '@common/components/Portal';
import { StickyBarContent } from '@common/components/StickyBar';
import { useTranslations } from '@lang';
import { Routes } from '@common/constants';
import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { Guest } from 'shared-types/Basket';
import { AddExistingGuestsV2 } from '../components/AddExistingGuestsV2';
import { MultiRoomGuestFieldV2 } from '../components/MultiRoomGuestFieldV2';
import { RoomRateFormValues } from '../RoomRateFormV2.schema';
import {
  NotesWrapper,
  StyledDateRangeIndicator,
} from '../../FormSteps/MultiRoomGuestDetailsStep/MultiRoomGuestDetailsStep.styles';
import { GuestDetailsStepV2Props } from './GuestDetailsStepV2.types';

type RoomPartial = {
  adults: number;
  children: number;
  roomNumber: number;
};

const routesMap = {
  room: Routes.ROOM_DETAIL,
  rate: Routes.RATE_DETAIL,
};

export const MultiRoomGuestDetailsStepV2 = ({
  basketGuests,
  loggedGuest,
  stickyBarId,
  selectedEntity,
  datePartyData,
  goToNextStep,
  addToBasket,
  metaEntity,
  availableExtras,
}: GuestDetailsStepV2Props) => {
  const { t } = useTranslations();
  const router = useRouter();
  const {
    register,
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
    watch,
  } = useFormContext<RoomRateFormValues>();

  const fieldArray = useFieldArray({ control, name: 'guests' });
  const guestValues = watch('guests') ?? [];
  const selectedGuests = guestValues.filter(
    (g) => g.firstName || g.lastName || g.externalSpaId || g.id
  ) as unknown as Guest[];

  useEffect(() => {
    if (selectedEntity) {
      return;
    }
    router.push({
      pathname: routesMap[metaEntity.type],
      query: { id: metaEntity.id },
    });
  }, [selectedEntity, router, metaEntity.type, metaEntity.id]);

  const hasExtras = availableExtras?.length;
  const rooms: RoomPartial[] = datePartyData?.rooms ?? [];

  const onSubmit = async (values: RoomRateFormValues) => {
    if (hasExtras) {
      goToNextStep();
    } else {
      await addToBasket({ guestDetails: values.guests, note: values.note });
      goToNextStep();
    }
  };

  return selectedEntity ? (
    <form noValidate>
      <SectionWrapper>
        {rooms.map((room: RoomPartial, index: number) => (
          <div key={room.roomNumber}>
            <h3>{`${t('common.room')} ${room.roomNumber}`}</h3>
            {index === 0 && (
              <AddExistingGuestsV2
                fieldArray={fieldArray}
                guestValues={guestValues}
                selectedGuests={selectedGuests}
                existingGuests={(basketGuests as Guest[]) ?? []}
                minimumGuests={room.adults}
                maximumGuests={room.adults}
                isGenderSpecific={false}
                guest={loggedGuest ?? null}
              />
            )}
            <MultiRoomGuestFieldV2
              fieldArray={fieldArray}
              roomNumber={room.roomNumber}
              roomAdults={room.adults}
              minimumGuests={room.adults}
              maximumGuests={room.adults}
              withLeadGuest={index === 0}
            />
            {room.children ? <>{`+${t('basket.children', { children: room.children })}`}</> : null}
          </div>
        ))}
        <NotesWrapper>
          <TextArea
            labelWidth="150px"
            label={t('rooms.notes')}
            id="additional-notes"
            input={register('note')}
            error={undefined}
          />
        </NotesWrapper>
      </SectionWrapper>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={metaEntity.title || ''}
          subtitle={
            <StyledDateRangeIndicator
              fromDate={datePartyData.fromDate}
              toDate={datePartyData.toDate}
            />
          }
          displayedPrice={selectedEntity?.pricePerStay || null}
          button={
            <Button
              isPrimary
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || isSubmitting}
            >
              {t(hasExtras ? 'spa.package.nextStep' : 'product.ctaButton')}
            </Button>
          }
        />
      </Portal>
    </form>
  ) : null;
};
